'use client';

import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';

import { cn } from '../utils/cn';
import {
  formControlFocusTransition,
  formControlSurfaceClass,
} from '../utils/form-control-styles';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Input } from './input';

export type PhoneInputProps = Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'value' | 'ref'
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
    onChange?: (value: RPNInput.Value) => void;
  };

const PHONE_INPUT_GROUP_CLASS = cn(
  'relative flex w-full rounded-md border focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 has-[input[aria-invalid=true]]:border-destructive has-[input[aria-invalid=true]]:focus-within:ring-destructive/30',
  formControlSurfaceClass,
  formControlFocusTransition,
);

const PhoneInput = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(({ className, onChange, value, disabled, onBlur, ...props }, ref) => {
  const handleContainerBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!onBlur) return;

    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;

    // iOS often leaves relatedTarget null when tapping the country trigger or overlay.
    if (!nextTarget) {
      window.setTimeout(() => {
        if (!event.currentTarget.isConnected) return;
        if (event.currentTarget.contains(document.activeElement)) return;
        onBlur(event as unknown as React.FocusEvent<HTMLInputElement>);
      }, 0);
      return;
    }

    onBlur(event as unknown as React.FocusEvent<HTMLInputElement>);
  };

  return (
    <div
      className={cn(PHONE_INPUT_GROUP_CLASS, className)}
      onBlur={handleContainerBlur}
    >
      <RPNInput.default
        ref={ref}
        className="flex h-10 w-full items-stretch"
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        inputComponent={InputComponent}
        smartCaret={false}
        disabled={disabled}
        value={value || undefined}
        onChange={(val) => onChange?.(val || ('' as RPNInput.Value))}
        {...props}
      />
    </div>
  );
});
PhoneInput.displayName = 'PhoneInput';

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ className, ...props }, ref) => (
  <Input
    className={cn(
      'h-10 rounded-e-md rounded-s-none border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-100',
      className,
    )}
    {...props}
    ref={ref}
  />
));
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
};

const SCROLLBAR_CLASSES =
  '[scrollbar-width:thin] [scrollbar-color:theme(colors.border)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:min-h-8';

const CountryListContent = ({
  countryList,
  selectedCountry,
  onChange,
  onSelectComplete,
  fillHeight = false,
}: {
  countryList: CountryEntry[];
  selectedCountry: RPNInput.Country;
  onChange: (country: RPNInput.Country) => void;
  onSelectComplete: () => void;
  /** When true, list area grows to fill container (e.g. mobile overlay) */
  fillHeight?: boolean;
}) => (
  <Command
    className={cn(
      'rounded-md border-0 bg-transparent shadow-none overflow-visible',
      fillHeight && 'flex h-full flex-col',
    )}
  >
    <CommandInput placeholder="Land suchen..." className="h-9 shrink-0" />
    <div
      role="listbox"
      tabIndex={0}
      className={cn(
        'min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain',
        fillHeight ? 'flex-1 min-h-0' : 'h-[280px]',
        SCROLLBAR_CLASSES,
      )}
      style={
        {
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overflow: 'auto',
        } as React.CSSProperties
      }
      data-vaul-no-drag
    >
      <CommandList className="max-h-none overflow-visible border-0 p-0 min-h-0">
        <CommandEmpty>Kein Land gefunden.</CommandEmpty>
        <CommandGroup className="p-1">
          {countryList
            .filter(
              (entry): entry is CountryEntry & { value: RPNInput.Country } =>
                !!entry.value,
            )
            .map(({ value, label }) => (
              <CountrySelectCommandItem
                key={value}
                country={value}
                countryName={label}
                selectedCountry={selectedCountry}
                onChange={onChange}
                onSelectComplete={onSelectComplete}
              />
            ))}
        </CommandGroup>
      </CommandList>
    </div>
  </Command>
);

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Always inline dropdown (no portal) so scroll works reliably on desktop and mobile
  React.useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (wrapperRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen]);

  const selectedCountryLabel = countryList.find(
    (option) => option.value === selectedCountry,
  )?.label;

  const triggerButton = (
    <Button
      type="button"
      variant="ghost"
      size="lg"
      className="flex h-10 shrink-0 items-center gap-1.5 rounded-s-md rounded-e-none border-0 border-r border-input bg-transparent px-3 shadow-none hover:bg-muted/50 focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-100"
      disabled={disabled}
      onPointerDown={(event) => {
        // Prevent input blur on touch devices when opening the country list.
        event.preventDefault();
      }}
      onClick={() => setIsOpen((o) => !o)}
      // The flag alone has no accessible name (DEV-539).
      aria-label={
        selectedCountryLabel
          ? `Land auswählen, aktuell ${selectedCountryLabel}`
          : 'Land auswählen'
      }
      aria-expanded={isOpen}
    >
      <span className="flex h-5 w-6 items-center justify-center shrink-0">
        <FlagComponent
          country={selectedCountry}
          countryName={selectedCountryLabel ?? selectedCountry}
        />
      </span>
      <ChevronsUpDown
        className={cn(
          '-mr-2 size-4 opacity-50',
          disabled ? 'hidden' : 'opacity-100',
        )}
      />
    </Button>
  );

  return (
    <div ref={wrapperRef} className="relative">
      {triggerButton}
      {isOpen && (
        <>
          {/* Mobile: list centered in viewport, same height as desktop, no dimmed background */}
          <div
            className="fixed inset-0 z-[10050] flex items-center justify-center p-4 sm:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Land auswählen"
            data-phone-country-overlay
          >
            <button
              type="button"
              className="absolute inset-0 bg-transparent"
              onClick={() => setIsOpen(false)}
              aria-label="Schließen"
            />
            <div
              className="relative z-10 w-full max-w-[300px] rounded-md border bg-popover text-popover-foreground shadow-lg"
              data-vaul-no-drag
              onClick={(e) => e.stopPropagation()}
            >
              <CountryListContent
                countryList={countryList}
                selectedCountry={selectedCountry}
                onChange={onChange}
                onSelectComplete={() => setIsOpen(false)}
              />
            </div>
          </div>
          {/* Desktop: dropdown below trigger */}
          <div
            className="absolute left-0 top-full z-[100] mt-1 w-[300px] rounded-md border bg-popover text-popover-foreground shadow-md hidden sm:block"
            data-vaul-no-drag
          >
            <CountryListContent
              countryList={countryList}
              selectedCountry={selectedCountry}
              onChange={onChange}
              onSelectComplete={() => setIsOpen(false)}
            />
          </div>
        </>
      )}
    </div>
  );
};

interface CountrySelectCommandItemProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country;
  onChange: (country: RPNInput.Country) => void;
  onSelectComplete: () => void;
}

function CountrySelectCommandItem({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectCommandItemProps) {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };
  const isSelected = country === selectedCountry;

  return (
    <CommandItem
      value={`${country} ${countryName} +${RPNInput.getCountryCallingCode(country)}`}
      onSelect={handleSelect}
      className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-sm"
    >
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 truncate text-sm">{countryName}</span>
      <span className="shrink-0 text-sm text-muted-foreground">
        +{RPNInput.getCountryCallingCode(country)}
      </span>
      <CheckIcon
        className={cn(
          'ml-auto size-4 shrink-0',
          isSelected ? 'opacity-100' : 'opacity-0',
        )}
      />
    </CommandItem>
  );
}

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];
  return (
    <span
      className={cn(
        'flex h-4 w-6 shrink-0 items-center justify-center overflow-hidden rounded-sm [&_svg:not([class*="size-"])]:size-full',
        !Flag && 'bg-foreground/20',
      )}
    >
      {Flag ? <Flag title={countryName} /> : null}
    </span>
  );
};

export { PhoneInput };
export { isValidPhoneNumber } from 'react-phone-number-input';
